import type { PricingTierName } from '@/features/billing/types';

/**
 * Narrows a pricing table to the columns the opener asked for, preserving the table's
 * own order. `undefined` means show everything, which is what a generic "Upgrade"
 * button wants; gates pass the narrower set their requirement implies.
 */
export const filterPricingTiers = <T extends { name: PricingTierName }>(
  all: T[],
  tiers: PricingTierName[] | undefined
): T[] => {
  if (!tiers) {
    return all;
  }

  const visible = all.filter((tier) => tiers.includes(tier.name));

  // A requested tier the table doesn't sell would leave nothing to show, so fall
  // back to the full set rather than an empty modal.
  return visible.length > 0 ? visible : all;
};

// Sized so cards keep roughly the width they have in the full four-column table. The
// modal shrinks to its columns; without this a one-column pitch inherits the width
// that was chosen to fit four.
const MODAL_WIDTH_BY_COLUMNS: Record<number, string> = {
  1: '420px',
  2: '700px',
  3: '900px',
  4: '1100px',
};

/**
 * Modal width for a column count, as a Chakra responsive value. Full-bleed on mobile;
 * capped at 90% through the medium range, where the grid has already folded to fewer
 * columns and the full width would overflow the viewport; exact once every column fits
 * side by side.
 */
export const getModalMinWidth = (columns: number) => {
  const width = MODAL_WIDTH_BY_COLUMNS[columns] ?? MODAL_WIDTH_BY_COLUMNS[4];

  return { base: '100vw', md: `min(${width}, 90%)`, lg: width };
};

/** "Compare Plans" reads oddly above a single card, so name the plan instead. */
export const getComparePlansHeading = (
  visible: { name: PricingTierName }[]
): string =>
  visible.length === 1 ? `Upgrade to ${visible[0].name}` : 'Compare Plans';
